import { useMemo } from "react";
import { Row, Col } from "antd";
import { Form } from "@dreambigger/design-system/src/components";
import {
  Step,
  ResponseStep,
  ReviewStepAssets,
} from "@dreambigger/shared/src/types";
import { StepProps } from "../pages/flows/[flowId]";
import StepWrapper from "./step-wrapper";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { useSegment } from "@dreambigger/shared/src/hooks";
import { isModernTheme } from "@dreambigger/shared/src/utils";
import { useRouter } from "next/router";
import { useApplication } from "../api";
import { StepReviewCard, AttestationSummary } from "../components";
import computeNextVisitedPath from "../utils/computeNextVisitedPath";

export default function Review({ step, brand, flow, progress }: StepProps) {
  const theme = brand.theme || "v1";
  const applicationHelper = useApplication(flow.financialInstitution.id);
  const application = useMemo(
    () => applicationHelper.find(flow.id, "draft"),
    [applicationHelper, flow]
  );

  // Lookahead: project what visitedPath will be once this step is submitted.
  // Closes the one-step-behind gap where the backend hasn't yet pruned
  // skipped steps from the path (e.g. user changed a conditional and the
  // flow skipped intermediate steps to land here).
  const effectiveVisitedPath = useMemo(() => {
    if (!application?.visitedPath) return undefined;
    return computeNextVisitedPath(
      application.visitedPath,
      step.slug,
      application.lastVisitedSlug
    );
  }, [application?.visitedPath, step.slug, application?.lastVisitedSlug]);

  const disabled = false;
  const [form] = Form.useForm();
  const router = useRouter();
  // Track analytics with Segment
  const segment = useSegment();
  const { flowId, stepSlug } = router.query;

  const wrapperProps = {
    step,
    brand,
    flow,
    progress,
    form,
    disabled,
  };

  const steps = flow.skins[0].steps;

  const assets: ReviewStepAssets = step.assets;

  // List of filtered keys for use in the filterKey function below.
  // const keyFilterList = ["lastName", "firstName"];
  const keyFilterList: string[] = assets?.keyFilterList || [];

  // If there is no application passed into this step, return loading state
  if (!application) {
    return null;
  }

  const handleBack = () => {
    router.push({
      query: {
        ...router.query,
        flowId,
        stepSlug: step.assets.previousSlug,
      },
    });
    segment.track({
      action: "Button Click",
      label: `Back - ${stepSlug as string}`,
    });
  };

  const searchForResponse = (responses: any, slug: string) => {
    for (var i = 0; i < responses.length; i++) {
      if (responses[i].slug === slug) {
        return responses[i];
      }
    }
    return null;
  };

  return (
    <StepWrapper {...wrapperProps}>
      <Row>
        <Col className={isModernTheme(theme) ? "w-100 pa-3 br-2" : "w-100"}>
          {/* STEP HEADERS displayed over primary color background - Start, Name, Etc */}
          {steps
            .filter(
              (s: Step) =>
                s.slug === "start" ||
                !effectiveVisitedPath ||
                effectiveVisitedPath.includes(s.slug)
            )
            // Add/replace cross-sell steps are forward-only: editing them here would mutate the cart.
            .filter(
              (s: Step) =>
                !(
                  s.type === "cross-sell" &&
                  ["add", "replace"].includes(
                    (s.assets as { mode?: string })?.mode ?? ""
                  )
                )
            )
            .map((step: Step, i: any) => {
              let response: ResponseStep | null = searchForResponse(
                application.responses,
                step.slug
              );

              // Add the application ID as the first item of the start step for the review screen.
              if (step.slug === "start" && response) {
                response.fields = {
                  applicationId: application.id,
                  ...response.fields,
                };
              }
              return (
                <StepReviewCard
                  key={step.slug}
                  step={step}
                  response={response}
                  i={i}
                  keyFilterList={keyFilterList}
                  isV2={isModernTheme(theme)}
                />
              );
            })}
        </Col>
        {/* DISCLOSURE ATTESTATION SUMMARY - its own section, above the "Ready to
            submit?" divider. Lists each agreed disclosure with the server
            timestamp; hides itself when the application has no agreed disclosures. */}
        <AttestationSummary
          flow={flow}
          applicationId={application.id}
          effectiveVisitedPath={effectiveVisitedPath}
          isV2={isModernTheme(theme)}
        />
        {/* CTA BOX - "Ready to submit?" Asks user if he/she wants to edit responses. */}
        {!assets.hideReadyText && (
          <Col className="flex flex-column bt b-lightPrimary w-100 pt-6 mt-4">
            <span className="f-5 fwt">Ready to submit?</span>
            <span>
              If you need to make changes, use the{" "}
              <span
                className="br-1 pa-1 darkenPrimaryBkd textPrimary pointer "
                style={{ backgroundColor: "#a2a2a2" }}
                onClick={handleBack}
              >
                <ArrowLeftOutlined /> back button
              </span>{" "}
              to return to previous steps.
            </span>
          </Col>
        )}
        {/* OPTIONAL DISCLAIMER TEXT - "By clicking submit..." */}
        {(assets.disclaimerTitle || assets.disclaimerText) && (
          <Col className="mt-4">
            {assets.disclaimerTitle && (
              <h5
                className="fwt"
                dangerouslySetInnerHTML={{
                  __html: assets.disclaimerTitle,
                }}
              />
            )}
            {assets.disclaimerText && (
              <p
                className="mb-0 f-2 lh-1 pl-2 bl"
                style={{ borderColor: "lightgray" }}
                dangerouslySetInnerHTML={{
                  __html: assets.disclaimerText,
                }}
              />
            )}
          </Col>
        )}
      </Row>
    </StepWrapper>
  );
}
